import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PublicClientApplication, EventType, type AuthenticationResult } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { ToastProvider } from '@babomrs/ui';
import { configureApiClient } from '@babomrs/api-client';
import { App } from './App';
import { msalConfig, tokenRequest } from './hooks/msalConfig';
import { apiBaseUrl } from './lib/runtimeConfig';
import { consumeSessionEnded, isPortalPath, landingAfterSessionEnd, markSessionEnded } from './lib/sessionEnd';
import { activeEntityHeaders } from './lib/activeEntity';
import './styles/globals.css';

const isDev = import.meta.env.VITE_AUTH_MODE === 'dev';

// --- MSAL instance (created ONCE, outside React tree per frontend-react.md) ---
const msalInstance = new PublicClientApplication(msalConfig);

msalInstance.initialize().then(() => {
  msalInstance.handleRedirectPromise().then((response) => {
    if (response) {
      msalInstance.setActiveAccount(response.account);
    } else {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }
    }
  }).catch((error) => {
    console.error('MSAL redirect error:', error);
  });
});

msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const result = event.payload as AuthenticationResult;
    msalInstance.setActiveAccount(result.account);
  }
});

configureApiClient({
  baseUrl: apiBaseUrl,
  getAccessToken: async (opts) => {
    if (isDev) return 'dev-token';

    const account = msalInstance.getActiveAccount();
    if (!account) {
      console.warn('MSAL: No active account — token will be empty');
      return '';
    }

    try {
      const response = await msalInstance.acquireTokenSilent({
        ...tokenRequest,
        account,
        // Set by the apiClient's 401 retry path to bypass the MSAL cache and
        // mint a fresh token from the refresh token / SSO session.
        forceRefresh: opts?.forceRefresh ?? false,
      });
      return response.accessToken;
    } catch (error) {
      // Silent acquisition failed. Don't redirect from here — return empty and
      // let the apiClient's 401 path own the single fallback (force-refresh +
      // retry once, then onUnauthorized → logoutRedirect to the login page).
      console.warn('MSAL: Silent token acquisition failed:', error);
      return '';
    }
  },
  // Spec §7 — every call names the active entity (X-Active-Client-Ref), and nothing for the own record.
  // The server re-checks the grant on every request; the header is only the request.
  getExtraHeaders: (): Record<string, string> => ({
    ...(isDev ? { 'X-Dev-Role': 'Client' } : {}),
    ...activeEntityHeaders(),
  }),
  // Last-resort 401 handler: fired only after the apiClient force-refreshed the
  // token and retried once and it is STILL Unauthorized — the session genuinely
  // cannot be renewed silently. Fires at most once per burst of 401s.
  //
  // Two things it deliberately does, each for its own reason:
  //
  //  1. It ignores a 401 raised from the PUBLIC marketing site. `/` … `/contact`
  //     are anonymous pages sharing this same api-client — ContactPage posts an
  //     enquiry through it — so an unguarded handler would throw a visitor who
  //     has no account at all out to the identity provider. Latent today (that
  //     endpoint is [AllowAnonymous]) and cheap to close, so closed.
  //
  //  2. It drops a breadcrumb before redirecting, because the logout round-trip
  //     cannot land the client anywhere useful on its own: Entra only returns
  //     them to a REGISTERED redirect URI, every one of which is a bare origin,
  //     and this origin's `/` is the marketing homepage. lib/sessionEnd.ts
  //     carries them the last step to the sign-in screen at /portal.
  //
  // The full logoutRedirect is KEPT rather than swapped for a local cache clear:
  // ending the identity-provider session is the point on a finance-profile
  // platform where a client may be signing in from a shared machine.
  onUnauthorized: () => {
    if (isDev) return; // dev token never expires; never bounce in dev
    if (!isPortalPath(window.location.pathname)) return;
    markSessionEnded();
    msalInstance
      .logoutRedirect({ postLogoutRedirectUri: window.location.origin })
      .catch((e) => console.error('MSAL logoutRedirect (401 handler) failed:', e));
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

// A client whose session ended is returned by Entra to `/` — the marketing
// homepage — because only bare origins are registered as redirect URIs. Carry
// them the last step, to the sign-in screen at /portal. Decided BEFORE the first
// render so no part of the marketing site is painted on the way past, and the
// app is not mounted only to be navigated away from.
const landing = landingAfterSessionEnd(window.location.pathname, consumeSessionEnded());

if (landing) {
  window.location.replace(landing);
} else {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <App />
          </ToastProvider>
        </QueryClientProvider>
      </MsalProvider>
    </StrictMode>,
  );
}
